// Service for the Attendance Dashboard "Mi equipo" on-demand recalculation
// (ASIS-5403 / REXASIS-002).
// POST /attendance/my-team/recalculations — recalculates attendance data for
// every employee supervised by the current user; returns the server timestamp
// so the UI never relies on the client clock to stamp "last updated".

import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';

import type { LC_RecalculateMyTeamOut, MyTeamRecalculationResult } from '../types/myTeamRecalculate.types';
import { mapMyTeamRecalculationResult } from '../mappers/myTeamRecalculate.mapper';

// Overrides the client-wide default timeout: the backend runs the recalculation SP once per
// distinct company in the supervised team, sequentially, and itself budgets up to 300s
// (RECALCULATION_COMMAND_TIMEOUT_SECONDS) for EACH run — mirrored here 1:1. A supervisor with
// several companies could still exceed this (the sequential loop is not bounded by this constant),
// which is a known limitation of the current backend design, not something the client can fix.
const RECALCULATE_TIMEOUT_MS = 300000;

export class MyTeamRecalculateService {
  async recalculate(signal?: AbortSignal): Promise<ApiResponse<MyTeamRecalculationResult>> {
    const response = await apiClient.post<LC_RecalculateMyTeamOut>(
      API_CONFIG.ENDPOINTS.ATTENDANCE_MY_TEAM_RECALCULATIONS,
      undefined,
      { signal, timeoutMs: RECALCULATE_TIMEOUT_MS },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapMyTeamRecalculationResult(response.data) };
  }
}

export const myTeamRecalculateService = new MyTeamRecalculateService();
